import { themeOptions } from '@theme';
import {
  Cell,
  Pie,
  type PieLabelRenderProps,
  PieChart as RechartsPieChart,
  ResponsiveContainer
} from 'recharts';
import type { ChartData } from 'types/common';
import Styled from './PieChart.styled';

const RADIAN = Math.PI / 180;

export interface PieChartProps {
  /** Resolved hex or CSS colors, one per slice */
  colors: readonly string[];
  data: ChartData[];
  height?: number;
  compact?: boolean;
}

interface PieChartSizeRadii {
  innerRadius: number;
  outerRadius: number;
  width: number;
  height: number;
}

const NORMAL_PIE_CHART_SIZE_RADII: PieChartSizeRadii = {
  innerRadius: 44,
  outerRadius: 72,
  width: 162,
  height: 162
};

function renderSliceLabel(props: PieLabelRenderProps) {
  const { cx, cy, midAngle, innerRadius, outerRadius, percent } = props;

  const inner = Number(innerRadius);
  const outer = Number(outerRadius);
  const radius = inner + (outer - inner) * 0.5;
  const x = Number(cx) + radius * Math.cos(-midAngle * RADIAN);
  const y = Number(cy) + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill={themeOptions.palette.common.white}
      fontSize={12}
      fontFamily={themeOptions.typography.fontFamily}
      fontWeight={themeOptions.typography.fontWeightSemiBold}
      textAnchor="middle"
      dominantBaseline="central">
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
}

/**
 * Pie chart component, using theme-aligned typography.
 */
function PieChart({ colors, data, height }: PieChartProps) {
  const {
    innerRadius,
    outerRadius,
    width,
    height: sizeHeight
  } = NORMAL_PIE_CHART_SIZE_RADII;

  return (
    <Styled.Wrapper width={width} height={height ?? sizeHeight}>
      <ResponsiveContainer width="100%" height="100%">
        <RechartsPieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            dataKey="value"
            innerRadius={innerRadius}
            label={renderSliceLabel}
            labelLine={false}
            nameKey="name"
            outerRadius={outerRadius}
            paddingAngle={1}>
            {data.map((entry, index) => (
              <Cell
                key={`slice-${entry.name}-${index}`}
                fill={colors[index % colors.length]}
              />
            ))}
          </Pie>
        </RechartsPieChart>
      </ResponsiveContainer>
    </Styled.Wrapper>
  );
}

export default PieChart;
